import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import { productService } from '../../services/products';
import { useForm, validationRules } from '../../hooks/useForm';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ImageUtils } from '../../utils/imageUtils';
import './Livestreams.css';

const LivestreamEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [alert, setAlert] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(null);
  const [thumbnailValidation, setThumbnailValidation] = useState(null);
  const [existingThumbnail, setExistingThumbnail] = useState(null);

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues
  } = useForm(
    {
      title: '',
      description: '',
      category: '',
      subcategory: '',
      scheduled_time: '',
      is_scheduled: false
    },
    {
      title: [validationRules.required, validationRules.minLength(5)],
      description: [validationRules.required, validationRules.minLength(10)],
      category: [validationRules.required]
    }
  );


  // Load subcategories when category changes
  const loadSubcategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      const subcategoriesData = await productService.getSubcategories(categoryId);
      setSubcategories(Array.isArray(subcategoriesData) ? subcategoriesData : (subcategoriesData?.results || []));
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setInitialLoading(true);

      // Load livestream data, products, and categories in parallel
      const [livestreamData, productsResponse, categoriesResponse] = await Promise.all([
        livestreamService.getLivestream(id),
        productService.getMyProducts(),
        productService.getCategories()
      ]);

      console.log('LivestreamEdit: Livestream data:', livestreamData);
      console.log('LivestreamEdit: Products response:', productsResponse);
      console.log('LivestreamEdit: Categories response:', categoriesResponse);

      // Set livestream data
      if (livestreamData) {
        setValues({
          title: livestreamData.title || '',
          description: livestreamData.description || '',
          category: livestreamData.category || '',
          subcategory: livestreamData.subcategory || '',
          scheduled_time: livestreamData.scheduled_start_time
            ? new Date(livestreamData.scheduled_start_time).toISOString().slice(0, 16)
            : '',
          is_scheduled: !!livestreamData.scheduled_start_time
        });

        // Load subcategories for the current category
        if (livestreamData.category) {
          loadSubcategories(livestreamData.category);
        }

        // Set existing thumbnail
        if (livestreamData.thumbnail) {
          setExistingThumbnail(livestreamData.thumbnail);
          setThumbnailPreview(livestreamData.thumbnail);
        }

        // Set selected products
        if (livestreamData.products && Array.isArray(livestreamData.products)) {
          const productIds = livestreamData.products.map(p =>
            typeof p === 'object' ? p.id : p
          );
          setSelectedProducts(productIds);
        }
      }

      // Set products
      const productsArray = Array.isArray(productsResponse)
        ? productsResponse
        : (productsResponse?.results || productsResponse?.data || []);
      console.log('LivestreamEdit: Products array:', productsArray);
      setProducts(productsArray);

      // Set categories
      const categoriesArray = Array.isArray(categoriesResponse)
        ? categoriesResponse
        : (categoriesResponse?.results || categoriesResponse?.data || []);
      setCategories(categoriesArray);

    } catch (error) {
      console.error('Error loading data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load livestream data. Please try again.'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [id, setValues, loadSubcategories]);

  useEffect(() => {
    loadData();
  }, [id, loadData]);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    handleChange('category', categoryId);
    handleChange('subcategory', ''); // Reset subcategory when category changes
    loadSubcategories(categoryId);
  };

  const handleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    setThumbnailError(null);
    setThumbnailValidation(null);

    if (file) {
      // Validate exact dimensions
      const validation = await ImageUtils.validateImageDimensions(file);
      setThumbnailValidation(validation);

      if (!validation.isValid) {
        setThumbnailError(validation.message);
        setThumbnailFile(null);
        setThumbnailPreview(existingThumbnail);
        return;
      }

      setThumbnailFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(existingThumbnail);
    setThumbnailError(null);
    setThumbnailValidation(null);
    // Reset the file input
    const fileInput = document.getElementById('thumbnail');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const onSubmit = async (formData) => {
    try {
      if (selectedProducts.length === 0) {
        setAlert({
          type: 'warning',
          message: 'Please select at least one product for the livestream.'
        });
        return;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      if (formData.subcategory) {
        formDataToSend.append('subcategory', formData.subcategory);
      }

      // Add each product ID separately
      selectedProducts.forEach(productId => {
        formDataToSend.append('products', productId);
      });

      if (formData.is_scheduled && formData.scheduled_time) {
        formDataToSend.append('scheduled_start_time', formData.scheduled_time);
      }

      // Only append thumbnail if a new one was selected
      if (thumbnailFile) {
        formDataToSend.append('thumbnail', thumbnailFile);
      }

      console.log('LivestreamEdit: Updating livestream with data:', {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        products: selectedProducts,
        scheduled_start_time: formData.is_scheduled ? formData.scheduled_time : null,
        thumbnail: thumbnailFile ? thumbnailFile.name : 'unchanged'
      });

      await livestreamService.updateLivestream(id, formDataToSend);

      setAlert({
        type: 'success',
        message: 'Livestream updated successfully!'
      });

      setTimeout(() => {
        navigate('/livestreams');
      }, 1500);
    } catch (error) {
      console.error('Error updating livestream:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to update livestream. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          // Handle field-specific errors
          const fieldErrors = Object.entries(error.response.data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }

      setAlert({
        type: 'danger',
        message: errorMessage
      });
    }
  };

  if (initialLoading) {
    return <LoadingSpinner text="Loading livestream data..." />;
  }

  return (
    <div className="min-h-screen bg-dark-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/livestreams')}
              className="w-10 h-10 rounded-full bg-dark-800 border border-dark-700 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
            >
              <i className="bi bi-arrow-left text-lg"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Edit Livestream</h1>
              <p className="text-gray-400 text-sm">Update your live event details</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 rounded-xl border border-dark-700 shadow-xl overflow-hidden">
          <div className="p-6 md:p-8">
            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
                className="mb-6"
              />
            )}

            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit);
            }}>
              {/* Basic Information */}
              <div className="mb-8">
                <h5 className="text-lg font-bold text-white flex items-center mb-6 pb-2 border-b border-dark-700">
                  <i className="bi bi-info-circle mr-2 text-primary-500"></i>
                  Basic Information
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="title" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                      Livestream Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full bg-dark-900 border ${errors.title ? 'border-red-500' : 'border-dark-700'} rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors`}
                      id="title"
                      value={values.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Enter an engaging title for your livestream"
                    />
                    {errors.title && (
                      <div className="text-red-500 text-sm mt-1">{errors.title}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full bg-dark-900 border ${errors.category ? 'border-red-500' : 'border-dark-700'} rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors appearance-none`}
                      id="category"
                      value={values.category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="" className="text-gray-500">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {errors.category && (
                      <div className="text-red-500 text-sm mt-1">{errors.category}</div>
                    )}
                  </div>

                  {/* Subcategory Selection */}
                  {values.category && (
                    <div>
                      <label htmlFor="subcategory" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                        Subcategory
                        {loadingSubcategories && (
                          <span className="ml-2 inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></span>
                        )}
                      </label>
                      <select
                        className="w-full bg-dark-900 border border-dark-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors appearance-none"
                        id="subcategory"
                        value={values.subcategory}
                        onChange={(e) => handleChange('subcategory', e.target.value)}
                        disabled={loadingSubcategories || subcategories.length === 0}
                      >
                        <option value="" className="text-gray-500">
                          {subcategories.length === 0
                            ? 'No subcategories available'
                            : 'Select Subcategory (Optional)'}
                        </option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                      <small className="block mt-1 text-gray-500 text-xs">
                        Optionally select a subcategory
                      </small>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label htmlFor="description" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={`w-full bg-dark-900 border ${errors.description ? 'border-red-500' : 'border-dark-700'} rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors h-32 resize-none`}
                      id="description"
                      rows="4"
                      value={values.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe what viewers can expect from your livestream"
                    ></textarea>
                    {errors.description && (
                      <div className="text-red-500 text-sm mt-1">{errors.description}</div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="thumbnail" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                      Thumbnail Image
                      {!existingThumbnail && <span className="text-red-500">*</span>}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2">
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-dark-700/50 transition-colors ${thumbnailError ? 'border-red-500 bg-red-500/10' : 'border-dark-700 bg-dark-900'}`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <i className="bi bi-cloud-upload text-3xl text-gray-500 mb-2"></i>
                              <p className="mb-1 text-sm text-gray-400"><span className="font-bold">Click to upload</span> or drag and drop</p>
                              <p className="text-xs text-gray-500">PNG, JPG or GIF (Max 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            id="thumbnail"
                            accept="image/*"
                            onChange={handleThumbnailChange}
                            name="thumbnail_upload"
                          />
                        </label>
                        
                        <div className="mt-2 text-sm">
                          <div className="text-primary-500 font-bold flex items-center mb-1">
                            <i className="bi bi-info-circle mr-1"></i>
                            Required: Exactly 480×720px
                          </div>
                          <div className="text-gray-500 text-xs">
                            {existingThumbnail ? 'Upload a new image to replace the current thumbnail' : 'Only accept 480×720px dimensions'}
                          </div>
                        </div>
                        {thumbnailError && (
                          <div className="text-red-500 text-sm mt-1">
                            {thumbnailError}
                          </div>
                        )}
                        {thumbnailValidation && thumbnailValidation.isValid && (
                          <div className="text-green-500 text-sm mt-1 flex items-center">
                            <i className="bi bi-check-circle mr-1"></i>
                            {thumbnailValidation.message}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-center md:justify-start">
                        {thumbnailPreview ? (
                          <div className="relative group">
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail preview"
                              className="rounded-lg object-cover border border-dark-700 w-32 h-48 md:w-40 md:h-60"
                            />
                            {thumbnailFile && (
                              <button
                                type="button"
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                                onClick={removeThumbnail}
                                title="Remove new thumbnail"
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            )}
                            {existingThumbnail && !thumbnailFile && (
                              <div className="absolute top-2 left-2">
                                <span className="px-2 py-1 bg-primary-500/80 backdrop-blur-sm text-white text-xs font-bold rounded flex items-center">
                                  <i className="bi bi-image mr-1"></i> Current
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            className="w-32 h-48 md:w-40 md:h-60 rounded-lg border-2 border-dashed border-dark-700 bg-dark-900 flex flex-col items-center justify-center text-gray-500"
                          >
                            <i className="bi bi-image text-3xl mb-2 opacity-50"></i>
                            <div className="text-xs text-center px-2">480×720px Preview</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </div>

                {/* Scheduling */}
                <div className="mb-8">
                  <div className="mb-6 pb-2 border-b border-dark-700">
                    <h5 className="text-lg font-bold text-white flex items-center">
                      <i className="bi bi-calendar-event mr-2 text-primary-500"></i>
                      Schedule
                    </h5>
                  </div>

                  <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
                    <div className="mb-4">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={values.is_scheduled}
                            onChange={(e) => setFieldValue('is_scheduled', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`block w-14 h-8 rounded-full transition-colors ${values.is_scheduled ? 'bg-primary-500' : 'bg-dark-700'}`}></div>
                          <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${values.is_scheduled ? 'transform translate-x-6' : ''}`}></div>
                        </div>
                        <div className="ml-3 text-white font-medium">
                          Schedule for later <span className="text-gray-500 text-sm font-normal ml-1">(otherwise available immediately)</span>
                        </div>
                      </label>
                    </div>

                    {values.is_scheduled && (
                      <div className="pl-2 border-l-2 border-dark-700 ml-2">
                        <label htmlFor="scheduled_time" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2 pl-4">
                          Scheduled Time <span className="text-red-500">*</span>
                        </label>
                        <div className="pl-4 max-w-md">
                          <input
                            type="datetime-local"
                            className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors"
                            id="scheduled_time"
                            value={values.scheduled_time}
                            onChange={(e) => handleChange('scheduled_time', e.target.value)}
                            min={new Date().toISOString().slice(0, 16)}
                          />
                          <small className="block mt-1 text-gray-500 text-xs">
                            Select when you want to start the livestream
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Selection */}
                <div className="mb-8">
                  <div className="mb-6 pb-2 border-b border-dark-700">
                    <h5 className="text-lg font-bold text-white flex items-center">
                      <i className="bi bi-box-seam mr-2 text-primary-500"></i>
                      Select Products for Bidding
                    </h5>
                  </div>

                  {(!products || products.length === 0) ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 p-4 rounded-lg flex items-center">
                      <i className="bi bi-exclamation-triangle mr-2 text-xl"></i>
                      <span>
                        You don't have any products yet.
                        <a href="/products/create" className="underline font-bold ml-1 hover:text-yellow-400">Create your first product</a>
                      </span>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-400 text-sm mb-4">
                        Select the products you want to feature in this livestream.
                        You can start bidding for these products during the live session.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {(products || []).map((product) => (
                          <div 
                            key={product.id} 
                            className={`group relative bg-dark-900 rounded-xl overflow-hidden border transition-all cursor-pointer ${selectedProducts.includes(product.id) ? 'border-primary-500 shadow-lg shadow-primary-500/20 scale-[1.02]' : 'border-dark-700 hover:border-primary-500/50 hover:-translate-y-1'}`}
                            onClick={() => handleProductSelection(product.id)}
                          >
                            <div className="relative h-48">
                              <img
                                src={ImageUtils.getProductImageUrl(product)}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                alt={product.name}
                                onError={(e) => {
                                  e.target.src = ImageUtils.getPlaceholderImage();
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent opacity-80"></div>
                              
                              <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                                {selectedProducts.includes(product.id) && (
                                  <span className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center shadow-lg animate-bounce-short">
                                    <i className="bi bi-check-lg text-lg"></i>
                                  </span>
                                )}
                              </div>
                              
                              <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
                                <div className="flex gap-1 flex-wrap">
                                  {(product.available_quantity ?? product.quantity ?? 0) === 0 && (
                                    <span className="px-2 py-1 bg-red-500/90 backdrop-blur-sm text-white text-xs font-bold rounded">
                                      Out of Stock
                                    </span>
                                  )}
                                  {!product.is_active && (
                                    <span className="px-2 py-1 bg-gray-600/90 backdrop-blur-sm text-white text-xs font-bold rounded">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <h6 className="font-bold text-white mb-1 line-clamp-1">{product.name}</h6>
                              <p className="text-gray-400 text-xs mb-3 line-clamp-2 min-h-[2.5em]">
                                {product.description || 'No description available'}
                              </p>
                              
                              <div className="flex justify-between items-center pt-3 border-t border-dark-700">
                                <span className="font-mono font-bold text-primary-400">
                                  ₹{parseFloat(product.base_price || product.price || 0).toLocaleString('en-IN')}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-bold flex items-center ${(product.available_quantity ?? product.quantity ?? 0) > 10
                                  ? 'bg-green-500/20 text-green-400'
                                  : (product.available_quantity ?? product.quantity ?? 0) > 0
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  <i className="bi bi-box mr-1"></i>
                                  {product.available_quantity ?? product.quantity ?? 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedProducts.length > 0 && (
                        <div className="mt-6 bg-primary-500/10 border border-primary-500/20 text-primary-400 p-4 rounded-lg flex items-center">
                          <i className="bi bi-info-circle mr-2 text-xl"></i>
                          <span className="font-medium">
                            {selectedProducts.length} product(s) selected for this livestream
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="pt-6 border-t border-dark-700 flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-6 py-2 rounded-lg border border-dark-700 text-gray-300 font-medium hover:bg-dark-700 hover:text-white transition-colors"
                    onClick={() => navigate('/livestreams')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-lg bg-primary-500 text-white font-bold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={isSubmitting || products.length === 0 || thumbnailError}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-dark border-t-transparent rounded-full animate-spin mr-2"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle mr-2"></i>
                        Update Livestream
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

  );
};

export default LivestreamEdit;





